import { filterXSS, type IFilterXSSOptions } from 'xss';

const markdownAllowList: NonNullable<IFilterXSSOptions['allowList']> = {
  a: ['class', 'href', 'title', 'target', 'rel', 'aria-label'],
  abbr: ['title'],
  blockquote: ['class'],
  br: [],
  code: ['class'],
  dd: ['class'],
  defs: ['id'],
  div: ['class'],
  ellipse: ['class', 'cx', 'cy', 'rx', 'ry'],
  em: ['class'],
  figcaption: ['class'],
  figure: ['class'],
  foreignobject: ['class', 'height', 'width', 'x', 'y'],
  g: ['class', 'id', 'transform'],
  h1: ['class', 'id'],
  h2: ['class', 'id'],
  h3: ['class', 'id'],
  h4: ['class', 'id'],
  h5: ['class', 'id'],
  h6: ['class', 'id'],
  hr: ['class'],
  iframe: ['allow', 'allowfullscreen', 'loading', 'referrerpolicy', 'src', 'title'],
  i: ['class'],
  img: ['alt', 'class', 'height', 'src', 'title', 'width'],
  li: ['class'],
  line: ['class', 'x1', 'x2', 'y1', 'y2'],
  mark: ['class'],
  math: ['display', 'xmlns'],
  mfrac: ['class'],
  mi: ['class'],
  mmultiscripts: ['class'],
  mn: ['class'],
  mo: ['class'],
  mover: ['class'],
  mpadded: ['class'],
  mphantom: ['class'],
  mroot: ['class'],
  mrow: ['class'],
  ms: ['class'],
  mspace: ['class'],
  msqrt: ['class'],
  mstyle: ['class'],
  msub: ['class'],
  msubsup: ['class'],
  msup: ['class'],
  mtable: ['class'],
  mtd: ['class'],
  mtext: ['class'],
  mtr: ['class'],
  munder: ['class'],
  munderover: ['class'],
  ol: ['class', 'start'],
  p: ['class', 'data-processed'],
  path: ['class', 'd', 'fill', 'marker-end', 'stroke', 'stroke-width'],
  polygon: ['class', 'points'],
  polyline: ['class', 'points'],
  pre: ['class'],
  rect: ['class', 'height', 'rx', 'ry', 'width', 'x', 'y'],
  s: ['class'],
  small: ['class'],
  span: ['aria-hidden', 'aria-label', 'class', 'data-processed', 'role', 'style'],
  strong: ['class'],
  svg: ['aria-roledescription', 'class', 'height', 'id', 'role', 'style', 'viewBox', 'width', 'xmlns', 'xmlns:xlink'],
  sub: ['class'],
  sup: ['class'],
  table: ['class'],
  tbody: ['class'],
  td: ['class', 'colspan', 'rowspan'],
  tfoot: ['class'],
  th: ['class', 'colspan', 'rowspan'],
  thead: ['class'],
  tr: ['class'],
  text: ['class', 'dy', 'text-anchor', 'x', 'y'],
  title: [],
  tspan: ['class', 'dy', 'x'],
  ul: ['class'],
  use: ['class', 'href', 'x', 'xlink:href', 'y'],
  annotation: ['encoding'],
  semantics: [],
};

const isAllowedMarkdownHref = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return true;
  if (trimmed.startsWith('#')) return true;
  return /^(?:https?:|mailto:|tel:)/i.test(trimmed);
};

const markdownSanitizerOptions: IFilterXSSOptions = {
  allowList: markdownAllowList,
  onTagAttr: (tag, name, value, isWhiteAttr) => {
    if (tag === 'div' && name.startsWith('data-course-video-')) return value;
    if (tag === 'a' && name === 'href' && isWhiteAttr && !isAllowedMarkdownHref(value)) {
      return '';
    }
    return undefined;
  },
  stripIgnoreTag: true,
  stripIgnoreTagBody: ['script', 'style'],
};

/**
 * The application viewer accepts versioned Markdown, not arbitrary HTML. Keep
 * the output safe even when a content file contains an HTML block or a link
 * with a dangerous protocol.
 */
export const sanitizeMarkdownHtml = (html: string): string => filterXSS(html, markdownSanitizerOptions);
